import { useT } from "../lib/useT";
import { useStore } from "../state/store";
import { CopyBlock } from "./CopyBlock";

/**
 * Blocking first-run state: the device is known to the Gateway but still needs
 * a human to approve it.
 *
 * This deserves a screen rather than a banner. It is the one moment where the
 * app genuinely cannot proceed without the user going somewhere else and doing
 * something, so it has to say plainly what to do — including the request id,
 * which is the exact argument `openclaw devices approve` needs.
 */
export function PairingScreen() {
  const state = useStore((store) => store.connectionState);
  const settings = useStore((store) => store.settings);
  const connection = useStore((store) => store.connection);
  const disconnect = useStore((store) => store.disconnect);
  const t = useT();

  const requestId = state.pairing?.requestId;
  // Any upgrade reason is a re-approval of a device the Gateway already knows,
  // which reads very differently from first-time pairing. `metadata-upgrade`
  // covers a changed client identity, the rest a changed grant.
  const isUpgrade =
    state.pairing?.reason === "scope-upgrade" ||
    state.pairing?.reason === "role-upgrade" ||
    state.pairing?.reason === "metadata-upgrade";
  const command = requestId ? `openclaw devices approve ${requestId}` : "openclaw devices approve";

  return (
    <div className="setup">
      <div className="setup-card">
        <div className="setup-logo" aria-hidden="true">
          🔐
        </div>
        <h1>{isUpgrade ? t("pairing.titleUpgrade") : t("pairing.title")}</h1>
        {/* The lead names the Gateway in bold inside a sentence, so the whole
            sentence including its markup is one translatable string. */}
        <p
          className="setup-lead"
          dangerouslySetInnerHTML={{
            __html: t(isUpgrade ? "pairing.leadUpgrade" : "pairing.lead", {
              url: settings?.url ?? "",
            }),
          }}
        />

        <ol className="steps">
          <li>
            <span className="step-index">1</span>
            <div>
              <p className="step-title">{t("pairing.step1")}</p>
              <CopyBlock value={command} />
              {!requestId ? (
                <p
                  className="field-hint"
                  dangerouslySetInnerHTML={{ __html: t("pairing.noRequestId") }}
                />
              ) : null}
            </div>
          </li>
          <li>
            <span className="step-index">2</span>
            <div>
              <p className="step-title">{t("pairing.step2")}</p>
              <p className="field-hint">{t("pairing.step2Hint")}</p>
            </div>
          </li>
        </ol>

        <dl className="detail-list">
          {requestId ? (
            <div>
              <dt>{t("pairing.requestId")}</dt>
              <dd>
                <CopyBlock value={requestId} inline />
              </dd>
            </div>
          ) : null}
          {state.deviceId ? (
            <div>
              <dt>{t("pairing.deviceId")}</dt>
              <dd>
                <CopyBlock value={state.deviceId} inline truncate />
              </dd>
            </div>
          ) : null}
          {state.pairing?.requestedScopes?.length ? (
            <div>
              <dt>{t("pairing.requestedScopes")}</dt>
              <dd className="detail-plain">{state.pairing.requestedScopes.join(", ")}</dd>
            </div>
          ) : null}
        </dl>

        <div className="waiting-row">
          <span className="spinner" />
          <span>{t("pairing.waiting")}</span>
        </div>

        <div className="sheet-actions" style={{ justifyContent: "space-between" }}>
          <button
            type="button"
            className="button-ghost"
            onClick={() => disconnect({ forgetDevice: true })}
          >
            {t("pairing.otherConnection")}
          </button>
          <button type="button" className="button-ghost" onClick={() => connection?.retryNow()}>
            {t("pairing.checkNow")}
          </button>
        </div>
      </div>
    </div>
  );
}
